import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Forward } from 'lucide-react'
import { useForwardMessage } from '@/features/communications/hooks'
import { useStaff } from '@/api/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatApiErrorMessage } from '@/lib/api-error'
import type { CommunicationMessage } from '@/features/communications/types'
import {
  type StaffSuggestion,
  StaffEmailField,
} from '@/features/communications/recipients'
import { splitEmails } from './formatting'
import { ComposeTextarea } from '@/components/compose-field'

export function ForwardMessageDialog({
  message,
  open,
  onOpenChange,
}: {
  message: CommunicationMessage
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {open && <ForwardMessageForm message={message} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

function ForwardMessageForm({
  message,
  onClose,
}: {
  message: CommunicationMessage
  onClose: () => void
}) {
  const [toField, setToField] = useState('')
  const [ccField, setCcField] = useState('')
  const [comment, setComment] = useState('')
  const staffQuery = useStaff()
  const mutation = useForwardMessage(message.uuid ?? '')

  const staffSuggestions: StaffSuggestion[] = (staffQuery.data ?? [])
    .filter((u) => !!u.email)
    .map((u) => ({ name: u.name ?? null, email: u.email }))

  const toEmails = splitEmails(toField)
  const ccEmails = splitEmails(ccField)
  const invalidEmail = [...toEmails, ...ccEmails].find((e) => !e.includes('@'))

  const handleSubmit = () => {
    if (toEmails.length === 0) {
      toast.error('Add at least one recipient')
      return
    }
    if (invalidEmail) {
      toast.error(`Invalid email: ${invalidEmail}`)
      return
    }
    mutation.mutate(
      {
        to_emails: toEmails,
        cc_emails: ccEmails.length > 0 ? ccEmails : undefined,
        comment: comment.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Forwarded')
          onClose()
        },
        onError: (err) => toast.error(formatApiErrorMessage('Forward failed', err)),
      },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Forward email</DialogTitle>
        <DialogDescription>
          Forwards from po@shopusars.com so any reply lands back on this thread.
          Suggestions show staff first; you can also type any email.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <StaffEmailField
          label="To"
          value={toField}
          onChange={setToField}
          staff={staffSuggestions}
          placeholder="lesley@shopusars.com"
          autoFocus
        />
        <StaffEmailField
          label="CC (optional)"
          value={ccField}
          onChange={setCcField}
          staff={staffSuggestions}
          placeholder="another@example.com"
        />
        <div>
          <label className="text-xs font-medium text-muted-foreground">Note (optional)</label>
          <ComposeTextarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="FYI — 10% of this invoice is CIA, please review."
            rows={4}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <LoadingButton
          loading={mutation.isPending}
          loadingText="Forwarding..."
          onClick={handleSubmit}
          disabled={toEmails.length === 0}
        >
          <Forward className="mr-1 h-3 w-3" /> Forward
        </LoadingButton>
      </DialogFooter>
    </>
  )
}
